import { useEffect, useMemo, useRef, useState, type WheelEvent } from 'react'
import { Layout, Spin } from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../utils/redux/store'
import {
  attemptGetPlaylistsList,
  attemptGetMoodsList,
  attemptGetStylesList,
  attemptGetDaytimesList,
  attemptGetBusinessesList,
  PlaylistOrderBy,
} from '../../utils/redux/playlistSlice'
import { attemptGetFavs } from '../../utils/redux/favsSlice'
import { OrderDirection } from '../../utils/dto'
import PlaylistSection from '../../components/PlaylistSection'
import SearchInput from '../../components/SearchInput/SearchInput'
import './Home.scss'
import { attemptGetStatistics } from '../../utils/redux/statisticsSlice'

const { Content } = Layout

type ChipType = 'mood' | 'style' | 'daytime' | 'business'
type ChipItem = {
  key: string
  id: string
  label: string
  type: ChipType
  image?: string
}

const TYPE_TO_SEGMENT: Record<ChipType, 'mood' | 'style' | 'daytime' | 'business'> = {
  mood: 'mood',
  style: 'style',
  daytime: 'daytime',
  business: 'business',
}

function asText(value: any): string {
  if (value == null) return ''
  if (typeof value === 'string' || typeof value === 'number') return String(value)
  if (Array.isArray(value)) return value.map(asText).filter(Boolean).join(' ')

  if (typeof value === 'object') {
    return (value.en ||
      value.ru ||
      value.fr ||
      Object.values(value).find((v) => typeof v === 'string') ||
      '') as string
  }

  return ''
}

function toNum(v: unknown): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function toInt(v: unknown): number {
  return Math.max(0, Math.floor(toNum(v)))
}

function parseDurationToSec(v: unknown): number {
  if (typeof v === 'number') {
    return v > 1_000_000 ? Math.floor(v / 1000) : Math.floor(v)
  }

  if (typeof v === 'string') {
    const s = v.trim()
    if (!s) return 0

    if (s.includes(':')) {
      const parts = s.split(':').map((x) => toInt(x))
      if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2]
      if (parts.length === 2) return parts[0] * 60 + parts[1]
    }

    const n = Number(s)
    if (Number.isFinite(n)) return n > 1_000_000 ? Math.floor(n / 1000) : Math.floor(n)
  }

  return 0
}

function getId(x: any): string {
  return String(x?._id ?? x?.id ?? '')
}

function getSongCountFromPlaylist(p: any): number {
  const direct =
    p?.songsCount ??
    p?.songs_count ??
    p?.totalSongs ??
    p?.total_songs ??
    p?.tracksCount ??
    p?.tracks_count ??
    p?.totalTracks ??
    p?.total_tracks

  if (direct !== undefined && direct !== null) return toInt(direct)

  if (Array.isArray(p?.songs)) return p.songs.length
  if (Array.isArray(p?.tracks)) return p.tracks.length
  if (Array.isArray(p?.items)) return p.items.length

  return 0
}

function getPlaylistDurationSec(p: any): number {
  const direct =
    p?.durationSec ??
    p?.durationSeconds ??
    p?.totalDurationSec ??
    p?.totalDurationSeconds ??
    p?.duration ??
    p?.totalDuration

  let sec = parseDurationToSec(direct)
  if (sec > 0) return sec

  const arr = Array.isArray(p?.songs) ? p.songs : Array.isArray(p?.tracks) ? p.tracks : []
  if (!arr.length) return 0

  sec = arr.reduce((acc: number, s: any) => {
    const sDur =
      s?.durationSec ??
      s?.durationSeconds ??
      s?.duration ??
      s?.lengthSec ??
      s?.length ??
      s?.time
    return acc + parseDurationToSec(sDur)
  }, 0)

  return sec
}

function formatInt(n: number) {
  return new Intl.NumberFormat('en-US').format(Math.max(0, Math.floor(n)))
}

function formatHoursFromSec(sec: number) {
  const h = sec / 3600
  if (!Number.isFinite(h) || h <= 0) return '0'
  return Number.isInteger(h) ? String(h) : h.toFixed(1)
}

export default function Home() {
  const { t } = useTranslation('home')
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { playlistsBrief, moods, styles, daytimes, businesses } = useAppSelector(
    (state) => state.playlist,
  )
  const statistics = useAppSelector((state) => state.statistics)
  const [activeChip, setActiveChip] = useState('')
  const chipsRef = useRef<HTMLDivElement | null>(null)
  const [canChipsLeft, setCanChipsLeft] = useState(false)
  const [canChipsRight, setCanChipsRight] = useState(false)

  const chips = useMemo<ChipItem[]>(() => {
    const make = (arr: any[] | undefined, type: ChipType): ChipItem[] =>
      (arr ?? [])
        .map((x: any) => ({
          key: `${type}:${String(x?._id ?? '')}`,
          id: String(x?._id ?? ''),
          label: asText(x?.title ?? x?.name),
          type,
          image: x?.image,
        }))
        .filter((x) => x.id && x.label)

    return [
      ...make(moods as any[], 'mood'),
      ...make(styles as any[], 'style'),
      ...make(daytimes as any[], 'daytime'),
      ...make(businesses as any[], 'business'),
    ]
  }, [moods, styles, daytimes, businesses])

  const onChipClick = (chip: ChipItem) => {
    setActiveChip(chip.key)
    const segment = TYPE_TO_SEGMENT[chip.type]

    navigate(`/playlist-collection/${segment}/${chip.id}`, {
      state: { title: chip.label, image: chip.image, type: chip.type },
    })
  }

  const updateChipsArrows = () => {
    const el = chipsRef.current
    if (!el) return
    const left = el.scrollLeft
    const max = el.scrollWidth - el.clientWidth
    setCanChipsLeft(left > 2)
    setCanChipsRight(left < max - 2)
  }

  const getChipsStep = () => {
    const el = chipsRef.current
    if (!el) return 320
    return Math.max(220, Math.floor(el.clientWidth * 0.8))
  }

  const scrollChips = (dir: -1 | 1) => {
    const el = chipsRef.current
    if (!el) return
    el.scrollBy({ left: dir * getChipsStep(), behavior: 'smooth' })
  }

  const onChipsWheel = (e: WheelEvent<HTMLDivElement>) => {
    const el = chipsRef.current
    if (!el) return
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault()
      el.scrollLeft += e.deltaY
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        dispatch(
          attemptGetPlaylistsList({
            skip: 0,
            limit: 200,
            oderBy: PlaylistOrderBy.TITLE,
            oderDirection: OrderDirection.DESC,
          }),
        ),
        dispatch(
          attemptGetMoodsList({
            skip: 0,
            limit: 200,
            oderBy: PlaylistOrderBy.TITLE,
            oderDirection: OrderDirection.DESC,
          }),
        ),
        dispatch(
          attemptGetStylesList({
            skip: 0,
            limit: 200,
            oderBy: PlaylistOrderBy.TITLE,
            oderDirection: OrderDirection.DESC,
          }),
        ),
        dispatch(
          attemptGetDaytimesList({
            skip: 0,
            limit: 200,
            oderBy: PlaylistOrderBy.TITLE,
            oderDirection: OrderDirection.DESC,
          }),
        ),
        dispatch(
          attemptGetBusinessesList({
            skip: 0,
            limit: 200,
            oderBy: PlaylistOrderBy.TITLE,
            oderDirection: OrderDirection.DESC,
          }),
        ),
        dispatch(attemptGetFavs()),
        dispatch(attemptGetStatistics()),
      ])
    }

    void fetchData()
  }, [dispatch])

  useEffect(() => {
    const el = chipsRef.current
    if (!el) return

    updateChipsArrows()

    const onScroll = () => updateChipsArrows()
    const onResize = () => updateChipsArrows()

    el.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)

    return () => {
      el.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
    }
  }, [chips.length])

  const uniquePlaylists = useMemo(() => {
    const all = [
      ...(playlistsBrief ?? []),
      ...(moods ?? []),
      ...(styles ?? []),
      ...(daytimes ?? []),
      ...(businesses ?? []),
    ] as any[]

    const map = new Map<string, any>()
    const withoutId: any[] = []

    for (const p of all) {
      const id = getId(p)
      if (id) map.set(id, p)
      else withoutId.push(p)
    }

    return [...Array.from(map.values()), ...withoutId]
  }, [playlistsBrief, moods, styles, daytimes, businesses])

  if (!playlistsBrief && !moods && !styles && !daytimes && !businesses) {
    return (
      <div className="home-loading">
        <Spin size="large" />
        <div style={{ marginTop: 12 }}>{t('loading', { defaultValue: 'Loading…' })}</div>
      </div>
    )
  }

  return (
    <Content style={{ paddingBottom: '200px' }} className="home-content">
      <div className="home-container">
        <div className="home-topbar">
          <div className="home-topbar__row">
            <SearchInput />

            <div className="home-stats">
              {formatInt(statistics.songs || 0)}{' '}
              {t('stats.songs', { defaultValue: 'songs' })}{' '}
              <span className="home-stats__sep">|</span> {formatInt((statistics.duration || 0) / 3600)}{' '}
              {t('stats.hours', { defaultValue: 'hours' })}{' '}
              <span className="home-stats__sep">|</span> {formatInt(statistics.playlists || 0)}{' '}
              {t('stats.playlists', { defaultValue: 'playlists' })}
            </div>
          </div>

          <div className="home-chipsSlider">
            <button
              type="button"
              className="home-chipsArrow"
              onClick={() => scrollChips(-1)}
              disabled={!canChipsLeft}
              aria-label={t('a11y.scrollChipsLeft', {
                defaultValue: 'scroll chips left',
              })}
            >
              ‹
            </button>

            <div className="home-chipsViewport">
              <div className="home-chips" ref={chipsRef} onWheel={onChipsWheel}>
                {chips.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    className={`home-chip ${activeChip === c.key ? 'is-active' : ''}`}
                    onClick={() => onChipClick(c)}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              className="home-chipsArrow"
              onClick={() => scrollChips(1)}
              disabled={!canChipsRight}
              aria-label={t('a11y.scrollChipsRight', {
                defaultValue: 'scroll chips right',
              })}
            >
              ›
            </button>
          </div>
        </div>

        {playlistsBrief && playlistsBrief.length > 0 && (
          <PlaylistSection
            title={t('playlists')}
            items={playlistsBrief}
            type="playlist"
          />
        )}

        {moods && moods.length > 0 && (
          <PlaylistSection title={t('moods')} items={moods} type="mood" />
        )}

        {styles && styles.length > 0 && (
          <PlaylistSection title={t('styles')} items={styles} type="style" />
        )}

        {daytimes && daytimes.length > 0 && (
          <PlaylistSection title={t('daytimes')} items={daytimes} type="daytime" />
        )}

        {businesses && businesses.length > 0 && (
          <PlaylistSection title={t('businesses')} items={businesses} type="business" />
        )}
      </div>
    </Content>
  )
}
